import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaArrowRight,
  FaCheckCircle,
  FaCopy,
  FaGift,
  FaMagic,
  FaTimes,
} from "react-icons/fa";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CandyWrapAnimation from "../CandyShop/animations/CandyWrapAnimation";
import "./CandyWrapCelebration.css";

const ONE_LINERS = [
  "Shelf goblin defeated.",
  "Formalwear applied to suspicious candy.",
  "Loose candy has entered its fancy era.",
  "Packaging department absolutely overreacted.",
];

const PHASE_ORDER = ["snatching", "cranking", "stamping", "bursting", "victory"];

const PHASE_LABELS = {
  snatching: "Grab",
  cranking: "Spin",
  stamping: "Seal",
  bursting: "Pop",
  victory: "Flex",
  complete: "Flex",
};

const truncateHash = (hash) => {
  if (!hash || hash.length < 18) {
    return hash || "";
  }

  return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
};

const CandyWrapCelebration = ({ receipt, isVisible = false, onClose }) => {
  const [isReadyToClose, setIsReadyToClose] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(PHASE_ORDER[0]);
  const [isHashCopied, setIsHashCopied] = useState(false);

  const oneLiner = useMemo(() => {
    const seed = String(
      receipt?.transactionIntentHash || receipt?.id || receipt?.pairDisplayName || "wrap"
    )
      .split("")
      .reduce((total, char) => total + char.charCodeAt(0), 0);

    return ONE_LINERS[seed % ONE_LINERS.length];
  }, [receipt?.id, receipt?.pairDisplayName, receipt?.transactionIntentHash]);

  const currentPhaseIndex = useMemo(
    () => PHASE_ORDER.indexOf(currentPhase === "complete" ? "victory" : currentPhase),
    [currentPhase]
  );

  const handleCopyHash = useCallback(async () => {
    if (!receipt?.transactionIntentHash) {
      return;
    }

    try {
      await navigator.clipboard.writeText(receipt.transactionIntentHash);
      setIsHashCopied(true);
    } catch (error) {
      console.error("Failed to copy wrap hash:", error);
    }
  }, [receipt?.transactionIntentHash]);

  const handleAnimationComplete = useCallback(() => {
    setIsReadyToClose(true);
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setIsReadyToClose(false);
      setCurrentPhase(PHASE_ORDER[0]);
      setIsHashCopied(false);
      return;
    }

    setIsReadyToClose(false);
    setCurrentPhase(PHASE_ORDER[0]);
    setIsHashCopied(false);
  }, [isVisible, receipt?.id]);

  useEffect(() => {
    if (!isHashCopied) {
      return undefined;
    }

    const timer = setTimeout(() => setIsHashCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [isHashCopied]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && receipt && (
        <motion.div
          key={receipt.id}
          className="candy-wrap-celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.24 }}
          role="dialog"
          aria-modal="true"
          aria-label="Candy wrap celebration"
        >
          <div
            className="candy-wrap-celebration-backdrop"
            onClick={onClose}
          />

          <motion.div
            className="candy-wrap-celebration-shell"
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
          >
            <div className="candy-wrap-celebration-topline">
              <div className="candy-wrap-status-stack">
                <Badge className="candy-wrap-celebration-badge">
                  <FaGift />
                  <span>{isReadyToClose ? "WRAPPED" : "WRAPPING"}</span>
                </Badge>
                <span className="candy-wrap-status-copy">
                  {PHASE_LABELS[currentPhase] || "Wrapping"} in progress
                </span>
              </div>

              <button
                type="button"
                className="candy-wrap-close"
                onClick={onClose}
                aria-label="Close celebration"
              >
                <FaTimes />
              </button>
            </div>

            <div className="candy-wrap-phase-rail" aria-hidden="true">
              {PHASE_ORDER.map((phaseName, index) => {
                const isComplete = currentPhaseIndex > index || currentPhase === "complete";
                const isActive =
                  phaseName === currentPhase ||
                  (currentPhase === "complete" && phaseName === "victory");

                return (
                  <div
                    key={phaseName}
                    className={`candy-wrap-phase-step ${
                      isComplete ? "is-complete" : ""
                    } ${isActive ? "is-active" : ""}`}
                  >
                    <span className="phase-dot" />
                    <span>{PHASE_LABELS[phaseName]}</span>
                  </div>
                );
              })}
            </div>

            <div className="candy-wrap-celebration-content">
              <div className="candy-wrap-copy">
                <span className="candy-wrap-kicker">
                  <FaMagic />
                  <span>{oneLiner}</span>
                </span>
                <h2>{receipt.pairDisplayName}</h2>
                <p>
                  {receipt.amount} {receipt.amount === 1 ? "piece" : "pieces"} just
                  got promoted from pocket chaos to proper presentation.
                </p>
                <div className="candy-wrap-flow">
                  <span>{receipt.beforeLabel}</span>
                  <FaArrowRight />
                  <span>{receipt.afterLabel}</span>
                </div>

                <div className="candy-wrap-meta-grid">
                  <div className="candy-wrap-meta-card">
                    <span>Amount</span>
                    <strong>x{receipt.amount}</strong>
                  </div>
                  <div className="candy-wrap-meta-card">
                    <span>Version</span>
                    <strong>{receipt.version}</strong>
                  </div>
                  <div className="candy-wrap-meta-card">
                    <span>Status</span>
                    <strong>{isReadyToClose ? "Sealed" : "Processing"}</strong>
                  </div>
                </div>
              </div>

              <CandyWrapAnimation
                key={receipt.id}
                pair={receipt}
                amount={receipt.amount}
                onComplete={handleAnimationComplete}
                onPhaseChange={setCurrentPhase}
              />
            </div>

            <div className={`candy-wrap-summary ${isReadyToClose ? "is-ready" : ""}`}>
              <div className="candy-wrap-summary-meta">
                <span>Wallet-submitted candy wrap</span>
                <strong>{receipt.afterLabel}</strong>
              </div>

              <div className="candy-wrap-summary-actions">
                <div className="candy-wrap-hash-row">
                  <span>{truncateHash(receipt.transactionIntentHash)}</span>
                  <button
                    type="button"
                    className="candy-wrap-hash-copy"
                    onClick={handleCopyHash}
                  >
                    {isHashCopied ? <FaCheckCircle /> : <FaCopy />}
                    <span>{isHashCopied ? "Copied" : "Copy hash"}</span>
                  </button>
                </div>

                <Button
                  variant="default"
                  size="lg"
                  type="button"
                  onClick={onClose}
                  className="candy-wrap-cta"
                >
                  Back To Wrapper
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CandyWrapCelebration;
